import React from 'react';
import { useData } from '../contexts/DataContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertCircle, CheckCircle2, Info, XCircle, RefreshCw, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const Alerts = () => {
  const { data, refreshData, isRefreshing } = useData();
  const { alerts } = data;

  const handleRefresh = async () => {
    await refreshData();
    toast.success('Alerts refreshed');
  };

  const handleResolve = (alertId) => {
    toast.success('Alert marked as resolved');
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-warning" />;
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-success" />;
      default:
        return <Info className="h-5 w-5 text-primary" />;
    }
  };

  const getAlertBadge = (type) => {
    const variants = {
      error: 'bg-destructive-light text-destructive border-destructive/20',
      warning: 'bg-warning-light text-warning border-warning/20',
      success: 'bg-success-light text-success border-success/20',
      info: 'bg-accent text-accent-foreground border-border',
    };

    return (
      <Badge variant="outline" className={variants[type] || variants.info}>
        {type}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Alerts</h1>
          <p className="text-muted-foreground mt-1">
            System notifications and warnings
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{alerts.filter(a => !a.resolved).length}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-warning" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolved</p>
                <p className="text-2xl font-bold">{alerts.filter(a => a.resolved).length}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-success" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Arabic Detected</p>
                <p className="text-2xl font-bold">8</p>
              </div>
              <Info className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critical</p>
                <p className="text-2xl font-bold">0</p>
              </div>
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Alerts</CardTitle>
          <CardDescription>All system notifications and events</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((alert) => (
                <TableRow key={alert.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getAlertIcon(alert.type)}
                      {getAlertBadge(alert.type)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{alert.category}</Badge>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <p className="text-sm">{alert.message}</p>
                    {alert.destination && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Destination: {alert.destination}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(alert.timestamp), 'MMM d, h:mm a')}
                  </TableCell>
                  <TableCell>
                    {alert.resolved ? (
                      <Badge variant="outline" className="bg-success-light text-success border-success/20">
                        Resolved
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-warning-light text-warning border-warning/20">
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {!alert.resolved && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolve(alert.id)}
                      >
                        Resolve
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Alert Categories */}
      <Card>
        <CardHeader>
          <CardTitle>Alert Categories</CardTitle>
          <CardDescription>Distribution by category type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { name: 'Arabic Address', count: 8, severity: 'medium' },
              { name: 'Cache Miss', count: 145, severity: 'low' },
              { name: 'Provider Timeout', count: 3, severity: 'high' },
              { name: 'Circuit Open', count: 1, severity: 'critical' },
              { name: 'FX Rate Stale', count: 0, severity: 'low' },
            ].map((cat) => (
              <div key={cat.name} className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{cat.name}</span>
                    <Badge 
                      variant="outline" 
                      className={{
                        critical: 'bg-destructive-light text-destructive border-destructive/20',
                        high: 'bg-warning-light text-warning border-warning/20',
                        medium: 'bg-accent text-accent-foreground border-border',
                        low: 'bg-muted text-muted-foreground border-border',
                      }[cat.severity]}
                    >
                      {cat.severity}
                    </Badge>
                  </div>
                </div>
                <div className="text-2xl font-bold">{cat.count}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Alerts;
